import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { GoogleIcon, FacebookIcon, SitemarkIcon } from './CustomIcons';
import { useNavigate } from "react-router";
import { useState } from 'react';
import { useNotify } from '../landing-page/components/Utils';
import DisplayLogo from '../landing-page/components/LogoDisplay';
import { useParams } from 'react-router';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';




const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  height: "65%",
  padding: theme.spacing(4),
  gap: theme.spacing(3),
  margin: 'auto',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const CustomContainer = styled(Stack)(({ theme }) => ({
  position: 'relative',
  minHeight: '100dvh',
  height: "auto",
  padding: theme.spacing(2),
  overflowY: 'auto',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

export default function ResetPassword() {

  
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = React.useState(false);
  const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate();
  const notify = useNotify()
  const {token} = useParams()

  const handleTogglePasswordVisibility = (passwordType) => {
    if (passwordType === "password") {
      setShowPassword((prev) => !prev);
    }
     if (passwordType === "passwordConfirm") {
      setShowConfirmPassword((prev) => !prev);
     }
  };
  

  const validateInputs = () => {
    const password = document.getElementById('password');
    const confirmPassword = document.getElementById('confirmPassword');
    let isValid = true;


    if (
      !password.value || 
      password.value.length < 7 || 
      !/[a-zA-Z]/.test(password.value) || !/\d/.test(password.value)
    ) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 7 characters long and include at least one letter.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

   
    if (!confirmPassword.value || confirmPassword.value !== password.value) {
      setConfirmPasswordError(true);
      setConfirmPasswordErrorMessage('Passwords do not match.');
      isValid = false;
    } else {
      setConfirmPasswordError(false);
      setConfirmPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleSubmit = async(event) => {
    event.preventDefault();

    if (passwordError || (!validateInputs()))  {
      return;
    }
    else {

         try {
            setLoading(true)
            const data = new FormData(event.currentTarget);

            const fetching = await fetch(`${process.env.REACT_APP_API_URL}/forgotpassword?token=${token}`, {
              "method": "PUT",
              "body": data,
            })
            const resp = await fetching.json()
            if (fetching.ok) {
              notify({message: resp,variant:"success",duration: 1000,horizontal: "right"})
              return navigate("/signin");
              } 
            else {
                notify({message: resp,variant:"warning",duration: 2000})
                return;
            }
    
         }
         catch(error) {
          notify({message: "cant sync with server please try again later",variant: "error",duration:2000})
          console.log(error)
          return;
         }
         finally {
          setLoading(false)
         }
          
          }
  }
  

  return (
    <>
      <CssBaseline />
      <CustomContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
           <DisplayLogo width={120} height={120}/>
          <Typography
            component="h1"
            variant="h2"
            textAlign={"center"}
            sx={{ width: '100%', fontSize: 'clamp(1rem, 10vw, 1.5rem)' }}
          >
            New Password
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
        
          
            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                required
                fullWidth
                name="password"
                placeholder="••••••"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                variant="outlined"
                error={passwordError}
                helperText={passwordErrorMessage}
                color={passwordError ? 'error' : 'primary'}
                slotProps={{ input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton  onClick={() => handleTogglePasswordVisibility("password")} edge="end">
                                      {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }
                              }}
                
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                placeholder="••••••"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                variant="outlined"
                error={confirmPasswordError}
                helperText={confirmPasswordErrorMessage}
                color={confirmPasswordError ? 'error' : 'primary'}
                slotProps={{ input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton onClick={() => handleTogglePasswordVisibility("passwordConfirm")} edge="end">
                                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }
                              }}
              />
            </FormControl>
            
            <Button
              loading={loading}
              type="submit"
              fullWidth
              variant="contained"
              onClick={validateInputs}
            >
              Save
            </Button>
          </Box>
         
          
        </Card>
      </CustomContainer>
    </>
  );
}



